import { Button, H5, H6 } from "@sellar-io/components";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { Icon, SpinnerLegacy } from "../../components";
import { IntersectingInfiniteLoadingButton } from "../../components/infiniteLoading/IntersectingInfiniteLoadingButton";
import {
  Prompt,
  PromptHeader,
  PromptContent,
} from "../../components/prompt/Prompt";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../components/table/TableLegacy";
import {
  NEW_DELIVERY_AREA,
  UPDATE_DELIVERY_AREA,
} from "../../constants/routes";
import { useDeliveryAreaList } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaList";
import { capitalize } from "../../utils/nativeLodash";

const sorter = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

export function DeliveryAreaListTable(): ReactElement {
  const deliveryAreaQuery = useDeliveryAreaList({
    limit: 20,
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const navigate = useNavigate();
  return (
    <>
      {deliveryAreaQuery.isLoading ? (
        <SpinnerLegacy />
      ) : (
        <>
          {deliveryAreaQuery.data?.pages[0]?.[0] ? (
            <TableLegacyContainer className={"overflow-auto"}>
              <TableLegacy>
                <TableLegacyTHead>
                  <TableLegacyTR>
                    <TableLegacyTH>Delivery area</TableLegacyTH>
                    <TableLegacyTH />
                  </TableLegacyTR>
                </TableLegacyTHead>
                <TableLegacyTBody>
                  {deliveryAreaQuery.data.pages.map((page) =>
                    page.map((deliveryArea) => (
                      <>
                        <TableLegacyTR>
                          <TableLegacyTD>
                            <div className="flex flex-col">
                              <H5>{deliveryArea.name}</H5>
                              {!!deliveryArea.DeliveryAreaDays.length ? (
                                <H6>
                                  Every{" "}
                                  {deliveryArea.weeklyCadence === 1
                                    ? "week"
                                    : `${deliveryArea.weeklyCadence} weeks`}{" "}
                                  on{" "}
                                  {deliveryArea.DeliveryAreaDays.sort(
                                    (a, b) => sorter[a.day] - sorter[b.day],
                                  )
                                    .map((deliveryAreaDay) =>
                                      capitalize(
                                        deliveryAreaDay.day.substring(0, 3),
                                      ),
                                    )
                                    .join(" & ")}
                                </H6>
                              ) : (
                                <H6>Ad-hoc delivery schedule</H6>
                              )}
                              {deliveryArea.orderCutOffDaysBefore &&
                                deliveryArea.orderCutOffTime && (
                                  <div className="flex flex-row">
                                    <H6>
                                      Order cut-off:{" "}
                                      {deliveryArea.orderCutOffDaysBefore}{" "}
                                      day(s) prior before{" "}
                                      {dayjs(deliveryArea.orderCutOffTime)
                                        .subtract(1, "hours")
                                        .get("hours")}
                                      :
                                      {dayjs(deliveryArea.orderCutOffTime).get(
                                        "minutes",
                                      ) === 0
                                        ? "00"
                                        : dayjs(
                                            deliveryArea.orderCutOffTime,
                                          ).get("minutes")}
                                    </H6>
                                  </div>
                                )}
                            </div>
                          </TableLegacyTD>
                          <TableLegacyTD>
                            <div
                              className={"flex cursor-pointer justify-end"}
                              onClick={() =>
                                navigate(
                                  UPDATE_DELIVERY_AREA.href({
                                    deliveryAreaId: deliveryArea.id,
                                  }),
                                )
                              }
                            >
                              <Icon icon={"PENCIL"} />
                            </div>
                          </TableLegacyTD>
                        </TableLegacyTR>
                      </>
                    )),
                  )}
                  <IntersectingInfiniteLoadingButton
                    query={deliveryAreaQuery}
                  />
                </TableLegacyTBody>
              </TableLegacy>
            </TableLegacyContainer>
          ) : (
            <Prompt
              className={"flex w-full flex-col gap-2"}
              type={"information"}
            >
              <PromptHeader type={"information"}>
                New! Reach more direct customers
              </PromptHeader>
              <PromptContent className={"flex flex-col gap-4"}>
                <p className={"text-black"}>
                  Setup your direct delivery schedule and get discovered by
                  buyers who fall within your serviceable area.
                </p>
                <Button
                  onClick={() => navigate(NEW_DELIVERY_AREA)}
                  className={twMerge(
                    "bg bg-blue-800 text-white hover:bg-blue-700",
                  )}
                >
                  Get started
                </Button>
              </PromptContent>
            </Prompt>
          )}
        </>
      )}
    </>
  );
}
